import { useEffect, useRef } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { ScanBarcode, X } from "lucide-react";

interface BarcodeScannerProps {
  open: boolean;
  onScan: (text: string) => void;
  onClose: () => void;
  title: string;
  hint: string;
  scannerId?: string;
  error?: string | null;
}

export default function BarcodeScanner({
  open,
  onScan,
  onClose,
  title,
  hint,
  scannerId = "barcode-reader",
  error,
}: BarcodeScannerProps) {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const runningRef = useRef(false);
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const timeout = setTimeout(async () => {
      const el = document.getElementById(scannerId);
      if (!el || cancelled) return;

      const scanner = new Html5Qrcode(scannerId);
      scannerRef.current = scanner;

      try {
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 280, height: 160 } },
          (decodedText) => {
            onScanRef.current(decodedText);
          },
          () => {}
        );
        if (!cancelled) {
          runningRef.current = true;
        } else {
          scanner.stop().catch(() => {});
          try { scanner.clear(); } catch {}
        }
      } catch {
        if (!cancelled) {
          // silently ignore camera errors — callers should handle UX
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      if (scannerRef.current && runningRef.current) {
        runningRef.current = false;
        scannerRef.current.stop().catch(() => {});
        try { scannerRef.current.clear(); } catch {}
        scannerRef.current = null;
      }
    };
  }, [open, scannerId]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 bg-black/80">
        <div className="flex items-center gap-2 text-white">
          <ScanBarcode className="size-5" />
          <span className="text-sm font-semibold">{title}</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="size-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
        >
          <X className="size-5 text-white" />
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center overflow-hidden">
        <div id={scannerId} className="w-full max-w-md" />
      </div>
      <div className="px-4 py-5 bg-black/80 text-center">
        {error ? (
          <p className="text-red-400 text-sm font-medium">{error}</p>
        ) : (
          <p className="text-white/60 text-sm">{hint}</p>
        )}
      </div>
    </div>
  );
}
